/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Activity, Loader2, TrendingUp } from 'lucide-react';
import { METRICS } from '@/components/health/metrics';
import { ProvidersSection } from '@/components/health/ProvidersSection';
import { SummarySection, MeasurementsSection } from '@/components/health/HealthSections';
import {
  listIoTProviders, connectIoTProvider, disconnectIoTProvider,
  pushHealthData, listHealthData, getHealthSummary,
} from '@/lib/sprint2';


const HealthDataPage = () => {
  const [providers, setProviders] = useState(null);
  const [summary, setSummary] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // provider_id -> bool
  const [form, setForm] = useState({ metric: 'heart_rate', value: '', unit: 'bpm' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, m] = await Promise.all([
        listIoTProviders(),
        getHealthSummary(),
        listHealthData({ limit: 30 }),
      ]);
      setProviders(p.providers || []);
      setSummary(s);
      setMeasurements(m.measurements || []);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Impossibile caricare i dati IoT');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setBusyFor = (id, val) => setBusy((b) => ({ ...b, [id]: val }));

  const connect = async (p) => {
    setBusyFor(p.id, true);
    try {
      const res = await connectIoTProvider(p.id, true);
      toast.success(res.message || 'Provider collegato');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore collegamento');
    } finally { setBusyFor(p.id, false); }
  };
  const disconnect = async (p) => {
    setBusyFor(p.id, true);
    try {
      await disconnectIoTProvider(p.id);
      toast.success('Provider scollegato');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally { setBusyFor(p.id, false); }
  };

  const submitMeasurement = async (e) => {
    e.preventDefault();
    const v = Number(form.value);
    if (Number.isNaN(v)) { toast.error('Valore non valido'); return; }
    setSaving(true);
    try {
      await pushHealthData({
        metric: form.metric,
        value: v,
        unit: form.unit || undefined,
        provider: 'manual',
      });
      toast.success('Valore registrato');
      setForm({ ...form, value: '' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="health-data-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2">
          <Activity className="h-6 w-6 text-primary" /> Dati sanitari IoT
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Collega dispositivi indossabili o inserisci manualmente i tuoi valori. I dati sono privati e visibili solo a te (e ai medici a cui li condividi durante una visita).
        </p>
      </div>

      <ProvidersSection providers={providers} loading={loading} busy={busy} onConnect={connect} onDisconnect={disconnect} />

      {/* Manual entry */}
      <section className="space-y-3">
        <h2 className="font-display text-lg font-semibold">Inserisci valore manuale</h2>
        <Card>
          <CardContent className="p-4">
            <form onSubmit={submitMeasurement} className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_auto]" data-testid="iot-manual-form">
              <div>
                <Label className="text-xs text-muted-foreground">Metrica</Label>
                <Select value={form.metric} onValueChange={(v) => {
                  const meta = METRICS.find((mm) => mm.key === v);
                  setForm({ ...form, metric: v, unit: meta?.unit || '' });
                }}>
                  <SelectTrigger className="mt-1" data-testid="iot-metric-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METRICS.map((mm) => (
                      <SelectItem key={mm.key} value={mm.key}>{mm.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Valore</Label>
                <Input
                  type="number" step="0.1" required
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder="es. 72"
                  className="mt-1"
                  data-testid="iot-value-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Unità</Label>
                <Input
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="mt-1"
                  data-testid="iot-unit-input"
                />
              </div>
              <div className="self-end">
                <Button type="submit" disabled={saving || !form.value} data-testid="iot-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <TrendingUp className="h-4 w-4" />}
                  Salva
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </section>

      <SummarySection loading={loading} summary={summary} />
      <MeasurementsSection loading={loading} measurements={measurements} />
    </div>
  );
};

export default HealthDataPage;
